import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Link,
  Skeleton,
  Box,
  CircularProgress
} from '@mui/material';
import { sponsor as sponsorApi, gallery as galleryApi } from '../../utils/api';
import PublicNavbar from '../../components/shared/PublicNavbar';

const SponsorCard = ({ sponsor }) => {
  const { t } = useTranslation('sponsors');
  
  const extractDomain = (url) => {
    try {
      const domain = new URL(url).hostname;
      return domain.replace(/^www\./, '');
    } catch (e) {
      return url;
    }
  };
  
  return (
    <Box>
      <Link 
        href={sponsor.website} 
        target="_blank" 
        rel="noopener noreferrer"
        sx={{ 
          textDecoration: 'none',
          display: 'block',
          '&:hover': {
            '& img': {
              transform: 'scale(1.02)',
              transition: 'transform 0.2s ease-in-out'
            }
          }
        }}
      >
        <CardMedia
          component="img"
          height="240"
          image={sponsor.logo}
          alt={sponsor.name}
          sx={{ 
            borderRadius: 1,
            mb: 1,
            objectFit: 'contain',
            backgroundColor: 'grey.100',
            p: 2,
            transition: 'transform 0.2s ease-in-out'
          }}
        />
        <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
          {sponsor.name.toUpperCase()}
        </Typography>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          paragraph
          sx={{ 
            whiteSpace: 'pre-line',
            mb: 2
          }}
        >
          {sponsor.description}
        </Typography>
      </Link>
      <Typography 
        variant="body2" 
        sx={{ 
          color: 'error.main',
          cursor: 'pointer',
          '&:hover': {
            textDecoration: 'underline'
          }
        }}
        onClick={() => window.open(sponsor.website, '_blank', 'noopener,noreferrer')}
      >
        {extractDomain(sponsor.website)}
      </Typography>
    </Box>
  );
};

const LoadingSkeleton = () => (
  <Box>
    <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 1, mb: 1 }} />
    <Skeleton variant="text" height={32} width="80%" />
    <Skeleton variant="text" height={20} width="100%" />
    <Skeleton variant="text" height={20} width="60%" />
  </Box>
);

const SponsorsPage = () => {
  const { t } = useTranslation('sponsors');
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [backgroundImage, setBackgroundImage] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [sponsorsData, galleryData] = await Promise.all([
          sponsorApi.getPublic(), // Using our public API method
          galleryApi.getPublicRandomImage() // Using our public API method
        ]);
        
        // Ensure sponsorsData is an array
        const sponsors = Array.isArray(sponsorsData) ? sponsorsData : [];
        setSponsors(sponsors);
        
        // Fix for nested URL structure
        if (galleryData && galleryData.url) {
          if (typeof galleryData.url === 'string') {
            setBackgroundImage(galleryData.url);
          } else if (galleryData.url.url) {
            setBackgroundImage(galleryData.url.url);
          }
        }
        setError(null);
      } catch (err) {
        // Don't log errors on public pages
        setError(t('messages.error'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [t]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const mainSponsors = sponsors.filter(sponsor => sponsor.type === 'main');
  const technicalSponsors = sponsors.filter(sponsor => sponsor.type === 'technical');
  const supportSponsors = sponsors.filter(sponsor => sponsor.type === 'support');

  const renderSponsorSection = (title, sponsorsList) => (
    <Box mb={8}>
      <Typography 
        variant="h2" 
        component="h2" 
        gutterBottom
        sx={{ 
          fontWeight: 'bold',
          position: 'relative',
          display: 'inline-block',
          color: 'white',
          fontSize: { xs: '1.5rem', sm: '1.5rem', md: '2rem' },
          '&:after': {
            content: '""',
            position: 'absolute',
            bottom: -8,
            left: 0,
            width: '100%',
            height: 4,
            backgroundColor: 'error.main'
          }
        }}
      >
        {title}
      </Typography>
      <Grid container spacing={4} sx={{ mt: 0 }}>
        {sponsorsList.map((sponsor) => (
          <Grid item key={sponsor._id} xs={6} sm={6} md={3}>
            <SponsorCard sponsor={sponsor} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  return (
    <Box sx={{ 
      position: 'relative', 
      overflow: 'hidden', 
      bgcolor: 'black', 
      color: 'white', 
      minHeight: '100vh' 
    }}>
      {backgroundImage && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.15,
            zIndex: 0
          }}
        />
      )}
      <PublicNavbar />
      <Container maxWidth="xl" sx={{ py: 12, position: 'relative', zIndex: 1 }}>
        {mainSponsors.length > 0 && renderSponsorSection(t('types.main'), mainSponsors)}
        {technicalSponsors.length > 0 && renderSponsorSection(t('types.technical'), technicalSponsors)}
        {supportSponsors.length > 0 && renderSponsorSection(t('types.support'), supportSponsors)}
      </Container>
      
      {/* Horizontal Line */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '20px',
          backgroundColor: 'error.main',
          zIndex: 0
        }}
      />
    </Box>
  );
};

export default SponsorsPage; 